import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Skeleton,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  TrendingUp as RevenueIcon,
  ConfirmationNumber as TicketIcon,
  Folder as ProjectIcon,
  Warning as WarningIcon,
  ArrowForward as ArrowIcon,
  Refresh as RefreshIcon,
  Subscriptions as SubscriptionsIcon
} from '@mui/icons-material';
import api from '../api/apiConfig';
import { useTheme } from '../contexts/ThemeContext';
import { useRecordDialog } from '../contexts/RecordDialogContext';
import StatusChip from './common/StatusChip';

const StatCard = ({ title, value, subtitle, icon, accentColor, onClick }) => {
  return (
    <Card 
      sx={{ 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s',
        borderLeft: `2px solid ${accentColor}`,
        height: '100%',
        '&:hover': onClick ? { 
          borderColor: accentColor
        } : {}
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 }, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 600,
              fontSize: '0.5rem'
            }}
          >
            {title}
          </Typography>
          <Box sx={{ color: accentColor, opacity: 0.7 }}>
            {React.cloneElement(icon, { sx: { fontSize: '1rem' } })}
          </Box>
        </Box>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontWeight: 700,
            fontSize: '1.5rem',
            color: 'text.primary',
            lineHeight: 1.2
          }}
        >
          {value}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              color: 'text.secondary',
              fontSize: '0.5625rem',
              mt: 0.25
            }}
          >
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const QuickStatCard = ({ value, label, accentColor }) => {
  return (
    <Card sx={{ textAlign: 'center', py: 1.5 }}>
      <Typography 
        sx={{ 
          fontFamily: '"JetBrains Mono", monospace', 
          fontWeight: 700,
          fontSize: '1.25rem',
          color: accentColor || 'text.primary'
        }}
      >
        {value}
      </Typography>
      <Typography 
        sx={{ 
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: '0.5rem',
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.1em'
        }}
      >
        {label}
      </Typography>
    </Card>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [urgentTickets, setUrgentTickets] = useState([]);
  const [overdueInvoices, setOverdueInvoices] = useState([]);
  const [expiringLicenses, setExpiringSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardLogoUrl, setDashboardLogoUrl] = useState('');
  const navigate = useNavigate();
  const { openRecord } = useRecordDialog();
  const { colors, mode } = useTheme();
  const isDark = mode === 'dark';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, ticketsRes, invoicesRes, licensesRes] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/urgent-tickets'),
        api.get('/dashboard/overdue-invoices'),
        api.get('/renewals/expiring', { params: { days: 60 } }).catch(() => ({ data: [] }))
      ]);

      setStats(statsRes.data);
      setUrgentTickets(ticketsRes.data);
      setOverdueInvoices(invoicesRes.data);
      setExpiringSubscriptions(licensesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await api.get('/public/branding');
        // Prefer the light-background variant when the interface is light: a
        // white wordmark on a white page is invisible, and an <img> cannot
        // inherit a colour. Falls back to the single logo when none is set.
        const wantLight = !isDark;
        const light = response.data?.dashboardLight;
        if (wantLight && light?.configured && light?.url) {
          setDashboardLogoUrl(`${light.url}?v=${Date.now()}`);
        } else if (response.data?.dashboard?.configured && response.data?.dashboard?.url) {
          setDashboardLogoUrl(`${response.data.dashboard.url}?v=${Date.now()}`);
        }
      } catch {
        // Ignore branding fetch errors in dashboard.
      }
    };
    fetchBranding();
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };


  const getDaysUntilExpiry = (expiryDate) => {
    if (!expiryDate) return null;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const diffTime = expiry - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const tableCellSx = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: '0.5625rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    padding: '6px 8px',
    color: isDark ? colors.textLight : colors.textDarkSecondary,
    borderBottom: `1px solid ${isDark ? colors.borderMuted : colors.borderLight}`,
    bgcolor: isDark ? colors.blackElevated : colors.whiteMuted
  };

  const tableBodyCellSx = {
    padding: '6px 8px',
    fontSize: '0.6875rem',
    borderBottom: `1px solid ${isDark ? colors.borderDark : colors.borderLight}`
  };

  if (loading) {
    return (
      <Box>
        <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Skeleton variant="rectangular" height={80} sx={{ bgcolor: isDark ? colors.borderDark : colors.borderLight }} />
            </Grid>
          ))}
        </Grid>
        <Grid container spacing={1.5}>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={250} sx={{ bgcolor: isDark ? colors.borderDark : colors.borderLight }} />
          </Grid>
          <Grid item xs={12} md={6}>
            <Skeleton variant="rectangular" height={250} sx={{ bgcolor: isDark ? colors.borderDark : colors.borderLight }} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 3,
              height: 20,
              bgcolor: colors.accent,
            }}
          />
          {/* The logo lives in the sidebar header, a few centimetres away.
              Repeating it here bought nothing and pushed the actual content
              down the page. */}
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontWeight: 700,
              fontSize: '1rem',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}
          >
            Dashboard
          </Typography>
        </Box>
        <Tooltip
          title="REFRESH"
          placement="left"
          PopperProps={{
            disablePortal: true,
            modifiers: [
              {
                name: 'preventOverflow',
                options: { boundary: 'viewport', padding: 8 }
              }
            ]
          }}
        >
          <IconButton onClick={fetchData} size="small" sx={{ color: 'text.secondary' }}>
            <RefreshIcon sx={{ fontSize: '1rem' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Open Tickets"
            value={stats?.tickets?.open || 0}
            subtitle={`${stats?.tickets?.thisMonth || 0} this month`}
            icon={<TicketIcon />}
            accentColor={colors.accent}
            onClick={() => navigate('/tickets?status=open')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Projects"
            value={stats?.projects?.active || 0}
            icon={<ProjectIcon />}
            accentColor={colors.statusBlue}
            onClick={() => navigate('/projects?status=in_progress')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Outstanding"
            value={formatCurrency(stats?.financial?.outstandingAmount)}
            subtitle={`${stats?.financial?.outstandingInvoices || 0} invoices`}
            icon={<WarningIcon />}
            accentColor={colors.statusRed}
            onClick={() => navigate('/invoices?status=sent')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Revenue (30d)"
            value={formatCurrency(stats?.financial?.revenueThisMonth)}
            subtitle={`${formatCurrency(stats?.financial?.pendingQuoteValue)} in quotes`}
            icon={<RevenueIcon />}
            accentColor={colors.statusGreen}
          />
        </Grid>
      </Grid>

      {/* Tables Row */}
      <Grid container spacing={1.5}>
        {/* Urgent Tickets */}
        <Grid item xs={12} md={6}>
          <Card>
            <Box sx={{ 
              px: 1.5, 
              py: 1,
              borderBottom: `1px solid ${isDark ? colors.borderDark : colors.borderLight}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography 
                sx={{ 
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Urgent Tickets
              </Typography>
              <Button 
                size="small" 
                endIcon={<ArrowIcon sx={{ fontSize: '0.75rem !important' }} />}
                onClick={() => navigate('/tickets?priority=critical')}
              >
                View All
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 240 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableCellSx}>Ticket</TableCell>
                    <TableCell sx={tableCellSx}>Client</TableCell>
                    <TableCell sx={tableCellSx}>Priority</TableCell>
                    <TableCell sx={tableCellSx}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {urgentTickets.length === 0 ? (
                    <TableRow>
                      <TableCell 
                        colSpan={4} 
                        align="center" 
                        sx={{ 
                          py: 3, 
                          borderBottom: 'none',
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.625rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'text.secondary'
                        }}
                      >
                        No urgent tickets
                      </TableCell>
                    </TableRow>
                  ) : (
                    urgentTickets.map((ticket) => (
                      <TableRow 
                        key={ticket.id}
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(186,35,46,0.03)' : 'rgba(186,35,46,0.02)'
                          },
                          '&:last-child td': { borderBottom: 'none' }
                        }}
                        onClick={() => openRecord({ type: 'ticket', id: ticket.id })}
                      >
                        <TableCell sx={tableBodyCellSx}>
                          <Typography 
                            sx={{ 
                              fontFamily: '"JetBrains Mono", monospace',
                              fontWeight: 600, 
                              fontSize: '0.6875rem' 
                            }}
                          >
                            {ticket.ticketNumber}
                          </Typography>
                          <Typography 
                            sx={{ 
                              fontSize: '0.5625rem',
                              color: 'text.secondary',
                              maxWidth: 120, 
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {ticket.subject}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Typography sx={{ fontSize: '0.625rem' }}>
                            {ticket.client?.name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <StatusChip status={ticket.priority} size="small" />
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <StatusChip status={ticket.status} size="small" />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>

        {/* Overdue Invoices */}
        <Grid item xs={12} md={6}>
          <Card>
            <Box sx={{ 
              px: 1.5, 
              py: 1,
              borderBottom: `1px solid ${isDark ? colors.borderDark : colors.borderLight}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography 
                sx={{ 
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Overdue Invoices
              </Typography>
              <Button 
                size="small" 
                endIcon={<ArrowIcon sx={{ fontSize: '0.75rem !important' }} />}
                onClick={() => navigate('/invoices?status=overdue')}
              >
                View All
              </Button>
            </Box>
            <TableContainer sx={{ maxHeight: 240 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={tableCellSx}>Invoice</TableCell>
                    <TableCell sx={tableCellSx}>Client</TableCell>
                    <TableCell sx={tableCellSx}>Due Date</TableCell>
                    <TableCell sx={{ ...tableCellSx, textAlign: 'right' }}>Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {overdueInvoices.length === 0 ? (
                    <TableRow>
                      <TableCell 
                        colSpan={4} 
                        align="center" 
                        sx={{ 
                          py: 3, 
                          borderBottom: 'none',
                          fontFamily: '"JetBrains Mono", monospace',
                          fontSize: '0.625rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                          color: 'text.secondary'
                        }}
                      >
                        No overdue invoices
                      </TableCell>
                    </TableRow>
                  ) : (
                    overdueInvoices.map((invoice) => (
                      <TableRow 
                        key={invoice.id}
                        hover
                        sx={{ 
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: isDark ? 'rgba(186,35,46,0.03)' : 'rgba(186,35,46,0.02)'
                          },
                          '&:last-child td': { borderBottom: 'none' }
                        }}
                        onClick={() => openRecord({ type: 'invoice', id: invoice.id })}
                      >
                        <TableCell sx={tableBodyCellSx}>
                          <Typography 
                            sx={{ 
                              fontFamily: '"JetBrains Mono", monospace',
                              fontWeight: 600, 
                              fontSize: '0.6875rem' 
                            }}
                          >
                            {invoice.invoiceNumber}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <Typography sx={{ fontSize: '0.625rem' }}>
                            {invoice.client?.name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={tableBodyCellSx}>
                          <StatusChip status="overdue" size="small" />
                        </TableCell>
                        <TableCell sx={{ ...tableBodyCellSx, textAlign: 'right' }}>
                          <Typography 
                            sx={{ 
                              fontFamily: '"JetBrains Mono", monospace',
                              fontWeight: 600, 
                              fontSize: '0.6875rem',
                              color: colors.statusRed
                            }}
                          >
                            {formatCurrency(invoice.amountDue)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Stats Row */}
      <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
        <Grid item xs={6} sm={3}>
          <QuickStatCard 
            value={stats?.clients?.active || 0} 
            label="Active Clients" 
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <QuickStatCard 
            value={stats?.assets?.total || 0} 
            label="Total Assets" 
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <QuickStatCard 
            value={stats?.assets?.expiring || 0} 
            label="Expiring (30d)" 
            accentColor={stats?.assets?.expiring > 0 ? colors.accent : undefined}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <QuickStatCard 
            value={stats?.financial?.pendingQuotes || 0} 
            label="Pending Quotes" 
          />
        </Grid>
      </Grid>

      {/* Expiring Licenses */}
      {expiringLicenses.length > 0 && (
        <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <Card>
              <Box sx={{ 
                px: 1.5, 
                py: 1,
                borderBottom: `1px solid ${isDark ? colors.borderDark : colors.borderLight}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SubscriptionsIcon sx={{ fontSize: '1rem', color: colors.accent }} />
                  <Typography 
                    sx={{ 
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em'
                    }}
                  >
                    Upcoming Renewals
                  </Typography>
                </Box>
                <Button 
                  size="small" 
                  endIcon={<ArrowIcon sx={{ fontSize: '0.75rem !important' }} />}
                  onClick={() => navigate('/renewals?expiringSoon=true')}
                >
                  View All
                </Button>
              </Box>
              <TableContainer sx={{ maxHeight: 200 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableCellSx}>License</TableCell>
                      <TableCell sx={tableCellSx}>Client</TableCell>
                      <TableCell sx={tableCellSx}>Expires</TableCell>
                      <TableCell sx={{ ...tableCellSx, textAlign: 'right' }}>Seats</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {expiringLicenses.map((sub) => {
                      const daysLeft = getDaysUntilExpiry(sub.expiryDate);
                      return (
                        <TableRow 
                          key={sub.id}
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: isDark ? 'rgba(186,35,46,0.03)' : 'rgba(186,35,46,0.02)'
                            },
                            '&:last-child td': { borderBottom: 'none' }
                          }}
                          onClick={() => openRecord({ type: 'license', id: sub.id })}
                        >
                          <TableCell sx={tableBodyCellSx}>
                            <Typography 
                              sx={{ 
                                fontFamily: '"JetBrains Mono", monospace',
                                fontWeight: 600, 
                                fontSize: '0.6875rem' 
                              }}
                            >
                              {sub.licenseNumber}
                            </Typography>
                            <Typography 
                              sx={{ 
                                fontSize: '0.5625rem',
                                color: 'text.secondary',
                                maxWidth: 180, 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {sub.name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>
                            <Typography sx={{ fontSize: '0.625rem' }}>
                              {sub.client?.name || '-'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={tableBodyCellSx}>
                            <Box 
                              sx={{ 
                                display: 'inline-block',
                                px: 0.75,
                                py: 0.25,
                                borderRadius: 0.5,
                                bgcolor: daysLeft <= 7 ? 'error.main' : daysLeft <= 30 ? 'warning.main' : 'info.main',
                                color: 'white',
                                fontFamily: '"JetBrains Mono", monospace',
                                fontSize: '0.5625rem',
                                fontWeight: 600
                              }}
                            >
                              {daysLeft}d
                            </Box>
                          </TableCell>
                          <TableCell sx={{ ...tableBodyCellSx, textAlign: 'right' }}>
                            <Typography 
                              sx={{ 
                                fontFamily: '"JetBrains Mono", monospace',
                                fontWeight: 600, 
                                fontSize: '0.6875rem'
                              }}
                            >
                              {sub.quantity}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Dashboard;
